// Bedienleiste über jeder Liste (Agent 5B, Runde 5): Zähler, Auswahl, Textsuche, Sortierung, Spaltenwahl. Eigenschaften: `listenVertrag.ts`.
// Die Leiste dient allen Listen: Smart Views und Leads (5B) sowie Kontakte, Aktivitäten, Opportunities (5C, 5D). Wer nur `sort`/`onSort`
// (ein Sortierfeld) und `spalten`/`gezeigt`/`onSpalten` übergibt, bekommt die Wahl mit einem Sortierfeld; Mehrfachsortierung, Sortierung
// der Ansicht und Standardspalten kommen über `BedienleisteZusatz` (additiv und optional, im Vertrag nachzutragen, siehe AENDERUNGSWUENSCHE.md).
// Taste `f` springt in die Suche, Esc leert sie.
import { useMemo, useRef, useState } from "react";
import { useTasten } from "../../werkzeuge/tasten";
import { Icon } from "../Icon";
import { SortierWahl } from "./SortierWahl";
import { SpaltenWahl } from "./SpaltenWahl";
import type { BedienleisteProps, ListenSortierung } from "./listenVertrag";
import "./Bedienleiste.css";

/** Additive Eigenschaften der Bedienleiste (Agent 5B). Alle optional. */
export interface BedienleisteZusatz {
  /** Eigene Sortierung (bis zu `sortMax` Felder) statt `sort`/`onSort`; leer = keine eigene Wahl */
  sortierung?: ListenSortierung[];
  onSortierung?: (s: ListenSortierung[]) => void;
  /** Sortierung der Liste ohne eigene Wahl (Smart View: „Reihenfolge der Ansicht“), nur zur Anzeige im Knopf */
  sortStandard?: ListenSortierung[];
  sortStandardName?: string;
  sortMax?: number;
  /** Hinweis unten in der Sortierwahl, z. B. „Sortiert nur die 600 geladenen Treffer“ */
  sortHinweis?: string | null;
  /** Spalten der Liste ohne eigene Wahl (für „Standard“ in der Spaltenwahl) */
  spaltenStandard?: string[];
  /** Gruppen, die in der Spaltenwahl beim Öffnen aufgeklappt sind */
  spaltenGruppenOffen?: string[];
}

function zaehlerText({ trefferZahl, einheit, geladen, laedt, ladeText }: Pick<BedienleisteProps, "trefferZahl" | "einheit" | "geladen" | "laedt" | "ladeText">) {
  if (trefferZahl !== null) {
    return (
      <>
        {`${trefferZahl.toLocaleString("de-DE")} ${trefferZahl === 1 ? einheit.eins : einheit.viele}`}
        {geladen !== undefined && geladen < trefferZahl ? <span className="leise"> · {geladen.toLocaleString("de-DE")} geladen</span> : null}
      </>
    );
  }
  return laedt && ladeText ? ladeText : "";
}

export function Bedienleiste(props: BedienleisteProps & BedienleisteZusatz) {
  const { vorn, hinweis, warnung, children, suche, auswahl, spalten, gezeigt, onSpalten, sort, sortFelder, onSort } = props;
  const [sortOffen, setSortOffen] = useState(false);
  const [spaltenOffen, setSpaltenOffen] = useState(false);
  const [sortAnker, setSortAnker] = useState<HTMLButtonElement | null>(null);
  const [spaltenAnker, setSpaltenAnker] = useState<HTMLButtonElement | null>(null);
  const sucheEingabe = useRef<HTMLInputElement>(null);

  useTasten({ f: () => sucheEingabe.current?.focus() }, !!suche);

  // Sortierung: Mehrfachwahl (sortierung/onSortierung) oder, wie im Vertrag, ein Feld (sort/onSort)
  const mehrfach = !!props.onSortierung;
  const eigene: ListenSortierung[] = mehrfach ? (props.sortierung ?? []) : sort ? [sort] : [];
  const sortHandler = mehrfach ? props.onSortierung : onSort ? (s: ListenSortierung[]) => onSort(s[0] ?? null) : undefined;
  const felder = sortFelder ?? spalten ?? [];
  const sortierbar = felder.filter((f) => f.sortierbar !== false);
  const standard = props.sortStandard ?? [];
  const wirksam = eigene[0] ?? standard[0] ?? null;
  const labelVon = (key: string) => felder.find((f) => f.key === key)?.label ?? key;
  const sortMax = mehrfach ? (props.sortMax ?? 3) : 1;
  const sortText = useMemo(() => {
    if (!wirksam) return "Sortieren";
    return labelVon(wirksam.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wirksam, felder]);
  const sortTitel = eigene.length
    ? `Sortiert nach ${eigene.map((s) => `${labelVon(s.key)} ${s.ab ? "absteigend" : "aufsteigend"}`).join(", dann ")}`
    : standard.length
      ? `${props.sortStandardName ?? "Reihenfolge der Ansicht"}: ${standard.map((s) => `${labelVon(s.key)} ${s.ab ? "absteigend" : "aufsteigend"}`).join(", dann ")}`
      : "Sortierung wählen";

  const hatSpalten = !!spalten && !!onSpalten && !!gezeigt;
  const anzahlAuswahl = auswahl?.anzahl ?? 0;

  return (
    <div className="liste-leiste bl">
      <div className="bl__links">
        {vorn}
        <span className="liste-leiste__zahl">{zaehlerText(props)}</span>
        {anzahlAuswahl > 0 && auswahl ? (
          <span className="bl__auswahl" role="status">
            <span>
              <span className="bl__auswahl-zahl">{anzahlAuswahl.toLocaleString("de-DE")}</span> markiert
            </span>
            <button type="button" aria-label="Markierung aufheben" title="Markierung aufheben (Esc)" onClick={auswahl.onAufheben}>
              <Icon name="schliessen" groesse={12} />
            </button>
          </span>
        ) : null}
        {auswahl?.onAlleMarkieren && (props.geladen ?? props.trefferZahl ?? 0) > anzahlAuswahl && (props.geladen ?? props.trefferZahl ?? 0) > 0 && anzahlAuswahl > 0 ? (
          <button type="button" className="bl__alle" onClick={auswahl.onAlleMarkieren}>
            Alle {(props.geladen ?? props.trefferZahl ?? 0).toLocaleString("de-DE")} geladenen markieren
          </button>
        ) : null}
        {hinweis}
        {warnung}
      </div>
      <div className="bl__rechts">
        {children}
        {suche ? (
          <label className={`suchfeld bl__suche${suche.wert ? " bl__suche--gefuellt" : ""}`}>
            <Icon name="suche" groesse={14} />
            <input
              ref={sucheEingabe}
              value={suche.wert}
              placeholder={suche.platzhalter ?? "In der Liste suchen …"}
              aria-label={suche.platzhalter ?? "In der Liste suchen"}
              onChange={(e) => suche.onAendern(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  if (suche.wert) suche.onAendern("");
                  else sucheEingabe.current?.blur();
                }
              }}
            />
            {suche.wert ? (
              <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Suche leeren" onClick={() => suche.onAendern("")}>
                <Icon name="schliessen" groesse={12} />
              </button>
            ) : (
              <kbd title="Taste f: in der Liste suchen">F</kbd>
            )}
          </label>
        ) : null}
        {sortHandler && sortierbar.length ? (
          <>
            <button
              ref={setSortAnker}
              type="button"
              className={`bl-knopf${eigene.length ? " bl-knopf--eigen" : ""}`}
              aria-haspopup="dialog"
              aria-expanded={sortOffen}
              title={sortTitel}
              onClick={() => setSortOffen((o) => !o)}
            >
              <Icon name={wirksam ? (wirksam.ab ? "pfeil-runter" : "pfeil-hoch") : "sortieren"} groesse={14} />
              <span className="bl-knopf__text">{sortText}</span>
              {eigene.length > 1 ? <span className="bl-knopf__zusatz">+{eigene.length - 1}</span> : null}
            </button>
            <SortierWahl
              anker={sortAnker}
              offen={sortOffen}
              onSchliessen={() => setSortOffen(false)}
              felder={felder}
              sortierung={eigene}
              standard={standard}
              standardName={props.sortStandardName ?? "Reihenfolge der Ansicht"}
              onSortierung={sortHandler}
              max={sortMax}
              hinweis={props.sortHinweis}
            />
          </>
        ) : null}
        {hatSpalten ? (
          <>
            <button
              ref={setSpaltenAnker}
              type="button"
              className={`bl-knopf${props.spaltenStandard && gezeigt && JSON.stringify(gezeigt) !== JSON.stringify(props.spaltenStandard) ? " bl-knopf--eigen" : ""}`}
              aria-haspopup="dialog"
              aria-expanded={spaltenOffen}
              title="Spalten wählen und ordnen"
              onClick={() => setSpaltenOffen((o) => !o)}
            >
              <Icon name="felder" groesse={14} />
              <span className="bl-knopf__text">Spalten</span>
            </button>
            <SpaltenWahl
              anker={spaltenAnker}
              offen={spaltenOffen}
              onSchliessen={() => setSpaltenOffen(false)}
              felder={spalten}
              gezeigt={gezeigt}
              standard={props.spaltenStandard}
              onSpalten={onSpalten}
              offeneGruppen={props.spaltenGruppenOffen}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
